<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="1200px"
    scrollable
  >
    <v-card>
      <v-card-item class="pa-0">
        <v-icon icon="mdi-text-box-search-outline" size="26" />
        <span class='review-workflow-components-title'>Workflow from Code</span>
        <v-btn
          icon="mdi-close"
          variant="plain"
          class="ml-auto"
          @click="toggleDialog(false)"
        />
      </v-card-item>

      <v-card-text class="review-workflow-components-card">
        <p class="mb-4 text-body-2 text-medium-emphasis">
          The AI has analyzed the selected code and extracted the following items.
          Review and select the items you wish to add to your workflow diagram.
        </p>
        <v-row>
          <v-col cols="12" md="7">
            <h3 class="column-header">Extracted Events</h3>
            <v-expansion-panels v-if="localData.length > 0" v-model="panel" multiple variant="accordion">
              <v-expansion-panel
                v-for="step in localData"
                :key="`${step.role}-${step.eventDescription}`"
                class="mb-1"
                :class="{ 'deselected-item': !step.isSelected }"
              >
                <v-expansion-panel-title class="pa-2">
                  <div class="mr-2">
                    <v-checkbox-btn
                      v-model="step.isSelected"
                      @click.stop
                      color="primary"
                    />
                  </div>
                  <v-icon :icon="getRoleIcon(step.role)" size="small" class="mr-3"></v-icon>
                  <span class="font-weight-medium">{{ step.eventDescription }}</span>
                  <v-chip size="small" class="mx-2">{{ step.role }}</v-chip>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <v-list density="compact" lines="two">
                    <v-list-item v-if="step.command && step.command.name" class="pa-1">
                      <template #prepend>
                        <v-icon color="blue-lighten-1">mdi-flash</v-icon>
                      </template>
                      <v-list-item-title class="font-weight-bold">Command</v-list-item-title>
                      <v-list-item-subtitle>{{ step.command.name }}</v-list-item-subtitle>
                      <p v-if="step.command.fields.length > 0" class="fields-text">
                        Fields: {{ step.command.fields.map(f => f.fieldName).join(', ') }}
                      </p>
                    </v-list-item>

                    <div v-if="step.queries && step.queries.length > 0">
                      <v-divider class="my-2"></v-divider>
                      <v-list-item v-for="(query, qIndex) in step.queries" :key="qIndex"  class="pa-1">
                        <template #prepend>
                          <v-icon color="green-lighten-1">mdi-magnify</v-icon>
                        </template>
                        <v-list-item-title class="font-weight-bold">Query</v-list-item-title>
                        <v-list-item-subtitle>{{ query.name }}</v-list-item-subtitle>
                        <p v-if="query.fields.length > 0" class="fields-text">
                          Fields: {{ query.fields.map(f => f.fieldName).join(', ') }}
                        </p>
                      </v-list-item>
                    </div>
                    <div v-if="step.gwt && step.gwt.length > 0">
                      <v-divider class="my-2"></v-divider>
                      <v-list-item v-for="gwtText in step.gwt" :key="gwtText" class="pa-1">
                        <template #prepend>
                          <v-icon color="purple-lighten-1">mdi-cogs</v-icon>
                        </template>
                        <v-list-item-title class="font-weight-bold">Given-When-Then</v-list-item-title>
                        <v-list-item-subtitle class="gwt-text">
                          {{ gwtText }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </div>
                  </v-list>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
            <div v-else class="text-center pa-8 text-medium-emphasis">
              No events were extracted from the code.
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <h3 class="column-header">Entities</h3>
            <v-list v-if="uniqueEntities.length > 0" class="entity-box rounded" density="compact">
              <v-list-item
                v-for="entity in uniqueEntities"
                :key="entity.name"
                :class="{ 'deselected-item': !entitySelection[entity.name] }"
              >
                <template #prepend>
                  <v-checkbox-btn
                    v-model="entitySelection[entity.name]"
                    color="primary"
                    class="mr-2"
                  />
                  <v-icon color="amber-darken-1">mdi-shape-outline</v-icon>
                </template>
                <v-list-item-title class="font-weight-bold">{{ entity.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="entity.fields.length > 0">
                  {{ entity.fields.map(f => f.fieldName).join(', ') }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <div v-else class="text-center pa-8 text-medium-emphasis">
              No entities were extracted.
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" size="large" @click="toggleDialog(false)">Cancel</v-btn>
        <v-btn variant="flat" color="primary" size="large" @click="confirmSelection">Confirm and Generate</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const { modelValue, extractedData } = defineProps({
  modelValue: { type: Boolean, required: true },
  extractedData: { type: Array, default: () => [] }
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const localData = ref([])
const panel = ref([])
const entitySelection = ref({})

const toggleDialog = (value) => {
  if (!value) {
    localData.value = []
    panel.value = []
    entitySelection.value = {}
  }
  emit('update:modelValue', value)
}

const getRoleIcon = (role) => {
  if (role.toLowerCase().includes('user') || role.toLowerCase().includes('customer')) {
    return 'mdi-account-outline'
  }
  if (role.toLowerCase().includes('system') || role.toLowerCase().includes('service')) {
    return 'mdi-cog-outline'
  }
  return 'mdi-account-tie-outline'
}

const uniqueEntities = computed(() => {
  const entityMap = new Map()
  localData.value.forEach((step) => {
    if (step.entities) {
      step.entities.forEach((entity) => {
        if (!entityMap.has(entity.name)) {
          entityMap.set(entity.name, {
            name: entity.name,
            fields: []
          })
        }
        const existingEntity = entityMap.get(entity.name)
        const existingFieldNames = new Set(existingEntity.fields.map((f) => f.fieldName))
        entity.fields.forEach((field) => {
          if (!existingFieldNames.has(field.fieldName)) {
            existingEntity.fields.push(field)
            existingFieldNames.add(field.fieldName)
          }
        })
      })
    }
  })
  return Array.from(entityMap.values())
})

const confirmSelection = () => {
  const selectedEvents = localData.value.filter((step) => step.isSelected)

  const selectedEntityNames = new Set(
    Object.keys(entitySelection.value)
      .filter((entityName) => entitySelection.value[entityName])
  )

  const workflowSteps = selectedEvents.map((event) => ({
    ...event,
    entities: event.entities.filter((entity) => selectedEntityNames.has(entity.name))
  }))

  emit('confirm', workflowSteps)
}

watch(() => extractedData, (newData) => {
  if (newData && newData.length > 0) {
    localData.value = newData.map((step) => ({ ...step, isSelected: true }))
    panel.value = [...Array(newData.length).keys()]

    const newEntitySelection = {}

    uniqueEntities.value.forEach((entity) => {
      newEntitySelection[entity.name] = true
    })
    entitySelection.value = newEntitySelection
  } else {
    localData.value = []
    panel.value = []
    entitySelection.value = {}
  }
}, { immediate: true, deep: true })

</script>

<style scoped>
.v-card {
  border: 1px solid var(--dark-2);
}

:deep(.v-card-item__content) {
  display: flex !important;
  align-items: center !important;
  column-gap: 12px;
  padding: 12px 16px;
}

.review-workflow-components-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--text-light) !important;
}

.review-workflow-components-card {
  padding: 20px 24px !important;
}

.column-header {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  color: var(--text-dark);
}

.fields-text {
  font-size: 10px;
  color: #666;
  margin-top: 4px;
  padding-left: 8px;
}

.gwt-text {
  font-size: 12px;
  white-space: normal;
  line-height: 1.4;
  margin-top: 4px;
}

.entity-box {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.deselected-item {
  opacity: 0.5;
  background-color: var(--light-1);
}

.v-expansion-panel, .v-list-item {
  transition: opacity 0.2s ease-in-out, background-color 0.2s ease-in-out;
}
</style>
